import React from "react";
import { BotIcon, ListChecksIcon, UserIcon, BadgeCheckIcon, FileTextIcon, GitBranchIcon, PlusIcon, XIcon, ChevronLeftIcon, ChevronRightIcon, BoxIcon } from "lucide-react";
import { BUILDER_EXAMPLE } from "../../../data/platform";
import { useWorkflowBuilder, AddOption } from "./useWorkflowBuilder";
import { cn } from "../../../src/lib/utils";
const KIND_ICON: Record<AddOption, BoxIcon> = {
  Agent: BotIcon,
  Requirement: ListChecksIcon,
  'Human Reviewer': UserIcon,
  Approval: BadgeCheckIcon,
  Document: FileTextIcon,
  Condition: GitBranchIcon
};
function Group({
  kind,
  items
}: {
  kind: AddOption;
  items: string[];
}) {
  if (items.length === 0) return null;
  const Icon = KIND_ICON[kind];
  return <div data-id="element-902" data-mp="eit4q01qvuyj">
      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-text-tertiary" data-id="element-903" data-mp="eijfieoixrtw">
        <Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-904" data-mp="is2oh9quhhh" />
        {kind === 'Agent' ? 'Agents' : kind === 'Requirement' ? 'Requirements' : kind}
      </p>
      <ul className="mt-2 flex flex-col gap-1.5" data-id="element-905" data-mp="1e1xgw5bae19k">
        {items.map(item => <li key={item} className={cn('rounded-lg border px-3 py-2 text-[13px]', kind === 'Agent' ? 'border-lime/20 bg-violet-soft font-medium text-lime' : 'border-hairline bg-panel text-text-primary')} data-id="element-906" data-mp="1a13tdd1raoh7">
            {item}
          </li>)}
      </ul>
    </div>;
}

/** Product-style example of configuring a due process. Changes are local only. */
export function WorkflowBuilder() {
  const {
    stages,
    activeIndex,
    activeStage,
    activeDrafts,
    announcement,
    select,
    addDraft,
    removeDraft,
    stageCount
  } = useWorkflowBuilder();
  return <div className="overflow-hidden rounded-2xl border border-hairline bg-panel shadow-[0_30px_80px_-50px_rgba(10,10,10,0.35)]" data-id="element-907" data-mp="153brbq7edlza">
      {/* Title bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-hairline bg-surface px-4 py-3 sm:px-5" data-id="element-908" data-mp="cl6e64ofe9ly">
        <div className="min-w-0" data-id="element-909" data-mp="7ltvlw4l7op6">
          <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-text-tertiary" data-id="element-910" data-mp="7ksi7l03mq4a">Workflow</p>
          <p className="truncate text-[15px] font-semibold text-text-primary" data-id="element-911" data-mp="2cnz9lj07zino">{BUILDER_EXAMPLE.name}</p>
        </div>
        <div className="flex items-center gap-2" data-id="element-912" data-mp="2hexoqf9h2apc">
          <span className="rounded-md border border-hairline bg-panel px-2 py-1 text-[11px] font-medium text-text-secondary" data-id="element-913" data-mp="2hfhay865nl96">{stages.length} stages</span>
          <span className="rounded-md bg-[#FDF3E7] px-2 py-1 text-[11px] font-semibold text-[#9A5B13]" data-id="element-914" data-mp="1oqjqpzidveds">Example workflow</span>
        </div>
      </div>

      <div className="grid lg:grid-cols-[260px_minmax(0,1fr)]" data-id="element-915" data-mp="1tpd4zu5hv9f0">
        {/* Stage list (desktop) */}
        <ol aria-label="Stages" className="hidden border-r border-hairline bg-surface/60 p-3 lg:block" data-id="element-916" data-mp="1xst36ux7phtt">
          {stages.map((stage, index) => {
          const active = index === activeIndex;
          return <li key={stage.name} data-id="element-917" data-mp="397y5hxtflecw" data-mpi={index}>
                <button type="button" onClick={() => select(index)} aria-current={active ? 'step' : undefined} className={cn('flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime', active ? 'bg-panel shadow-sm ring-1 ring-hairline' : 'hover:bg-panel/70')} data-id="element-918" data-mp="3e4f63rhthl6v">
                  <span className={cn('flex h-6 w-6 shrink-0 items-center justify-center rounded-full font-mono text-[11px]', active ? 'bg-lime text-white' : 'bg-elevated text-text-secondary')} data-id="element-919" data-mp="3egzb09843hp1">
                    {index + 1}
                  </span>
                  <span className={cn('min-w-0 flex-1 truncate text-[13px] font-medium', active ? 'text-text-primary' : 'text-text-secondary')} data-id="element-920" data-mp="30j17xzz8r7ou">
                    {stage.name}
                  </span>
                  <span className="text-[11px] text-text-tertiary" data-id="element-921" data-mp="2rje3f5b7tlrk">{stageCount(index)}</span>
                </button>
              </li>;
        })}
        </ol>

        {/* Stage selector (mobile / tablet) */}
        <div className="flex items-center gap-2 border-b border-hairline p-3 lg:hidden" data-id="element-922" data-mp="psavwowmgnb8">
          <button type="button" onClick={() => select(activeIndex - 1)} disabled={activeIndex === 0} aria-label="Previous stage" className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-hairline text-text-secondary disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime" data-id="element-923" data-mp="wu6axey8zxt0">
            <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-924" data-mp="rxfuurmg127l" />
          </button>
          <label htmlFor="builder-stage" className="sr-only" data-id="element-925" data-mp="e2n9vdnsattm">
            Stage
          </label>
          <select id="builder-stage" value={activeIndex} onChange={event => select(Number(event.target.value))} className="min-h-[44px] w-full min-w-0 rounded-lg border border-hairline bg-panel px-3 text-sm font-medium text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime" data-id="element-926" data-mp="ivzlmn7d764y">
            {stages.map((stage, index) => <option key={stage.name} value={index} data-id="element-927" data-mp="3xb7tlbgm35" data-mpi={index}>
                {index + 1}. {stage.name}
              </option>)}
          </select>
          <button type="button" onClick={() => select(activeIndex + 1)} disabled={activeIndex === stages.length - 1} aria-label="Next stage" className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-hairline text-text-secondary disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime" data-id="element-928" data-mp="3cokm9nxfzo3p">
            <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-929" data-mp="3vmdosp45lw0k" />
          </button>
        </div>

        {/* Stage detail */}
        <div className="p-4 sm:p-6" data-id="element-930" data-mp="3qmiuebl66oar">
          <div className="flex flex-wrap items-baseline justify-between gap-2" data-id="element-931" data-mp="3qlg4a5vo8pvw">
            <h3 className="text-lg font-semibold tracking-[-0.015em] text-text-primary" data-id="element-932" data-mp="969f30l1itv6">
              <span className="mr-2 font-mono text-sm text-lime" data-id="element-933" data-mp="48hoqb01nft3">0{activeIndex + 1}</span>
              {activeStage.name}
            </h3>
          </div>

          <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-934" data-mp="d8xpvdtbeaus">
            <Group kind="Agent" items={[...activeStage.agents]} data-id="element-935" data-mp="dah3ewnl67sv" />
            <Group kind="Requirement" items={[...activeStage.requirements]} data-id="element-936" data-mp="371ox02zzwojn" />
            <Group kind="Human Reviewer" items={activeStage.reviewer ? [activeStage.reviewer] : []} data-id="element-937" data-mp="3bz7be8ws7vz1" />
            <Group kind="Approval" items={activeStage.approval ? [activeStage.approval] : []} data-id="element-938" data-mp="3g0shnbet9c09" />
            <Group kind="Document" items={activeStage.document ? [activeStage.document] : []} data-id="element-939" data-mp="3kskchy5023w7" />
            <Group kind="Condition" items={activeStage.condition ? [activeStage.condition] : []} data-id="element-940" data-mp="3prn5gk7pgjnt" />
          </div>

          {activeDrafts.length ? <ul className="mt-5 flex flex-wrap gap-2" aria-label="Added placeholders" data-id="element-941" data-mp="3uonbiv4k1mex">
              {activeDrafts.map(draft => {
            const Icon = KIND_ICON[draft.kind];
            return <li key={draft.id} className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-lime/40 bg-violet-soft py-1 pl-2.5 pr-1 text-[13px] text-lime" data-id="element-942" data-mp="7kh9kd8riahm">
                    <Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-943" data-mp="29c3r7o6rkt1f" />
                    New {draft.kind.toLowerCase()}
                    <button type="button" onClick={() => removeDraft(draft.id)} aria-label={`Remove new ${draft.kind.toLowerCase()}`} className="inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime" data-id="element-944" data-mp="21u4hgokreal4">
                      <XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-945" data-mp="1o2zi7nlazkbb" />
                    </button>
                  </li>;
          })}
            </ul> : null}

          <div className="mt-6 border-t border-hairline pt-5" data-id="element-946" data-mp="2ft44jrsmihf6">
            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-text-tertiary" data-id="element-947" data-mp="2ftx4o9x2u8oc">Add to this stage</p>
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3" data-id="element-948" data-mp="2btdb1gbppn6y">
              {BUILDER_EXAMPLE.addOptions.map(option => {
              const Icon = KIND_ICON[option];
              return <button key={option} type="button" onClick={() => addDraft(option)} className="inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-hairline bg-panel px-3 text-left text-[13px] font-medium text-text-primary transition-colors hover:border-lime/40 hover:bg-violet-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime" data-id="element-949" data-mp="10aail7uhzyuh">
                    <PlusIcon className="h-3.5 w-3.5 shrink-0 text-lime" aria-hidden="true" data-id="element-950" data-mp="2linlezx0lers" />
                    <Icon className="h-3.5 w-3.5 shrink-0 text-text-tertiary" aria-hidden="true" data-id="element-951" data-mp="2qh8ca8qdmm3k" />
                    <span className="truncate" data-id="element-952" data-mp="3e7ch2jfawrw5">{option}</span>
                  </button>;
            })}
            </div>
          </div>
          <p className="sr-only" aria-live="polite" data-id="element-953" data-mp="3j774ue3s51cz">
            {announcement}
          </p>
        </div>
      </div>
    </div>;
}